import Vue from 'vue';
import sortBy from 'lodash/sortBy';
import {
  getFirebaseAuth,
  sendSignInLink,
  signInWithLink,
  isSignInLink,
  login,
  logout,
  updatePass,
  sendPasswordReset,
} from '../firebase/firebase';
import { MUTATION_REGISTER_SNAPSHOT_LISTENER, MUTATION_UNREGISTER_SNAPSHOT_LISTENER } from './constants';
import { types as featureSwitches } from './featureSwitches';
import {
  firestoreCollection,
  firestoreDocumentById,
  onSnapshot,
  getFieldValue,
  set,
  setMerge,
  createNewId,
} from '../firebase/firestore';

const MODULE_PREFIX = 'authentication';
const MUTATION_LOGIN = 'LOGIN';
const MUTATION_OBJECTIVES = 'OBJECTIVES';
const MUTATION_LOGOUT = 'LOGOUT';
const MUTATION_START_FETCH_ORGANIZATION_TAGS = 'START_FETCH_ORGANIZATION_TAGS';
const MUTATION_FETCH_ORGANIZATION_TAGS_FINISHED = 'FETCH_ORGANIZATION_TAGS_FINISHED';
const MUTATION_MARK_USER_JOURNAL_READ = 'MARKED_USER_JOURNAL_READ';
const MUTATION_USER_STATE = 'MUTATION_USER_STATE';
const MUTATION_FETCH_ORGANIZATIONS = 'FETCH_ORGANIZATIONS';
const MUTATION_START_LOGIN = 'START_LOGIN';
const MUTATION_END_LOGIN = 'END_LOGIN';
const MUTATION_SET_ID_TOKEN = 'SET_ID_TOKEN';
const MUTATION_ORGANIZATION_NAME = 'mutationOrganizationName';

const ACTION_LOGIN = 'login';
const ACTION_LOGIN_EXTENSION = 'loginExtension';
const ACTION_LOGOUT = 'logout';
const ACTION_SIGNIN = 'signin';
const ACTION_SIGNIN_WITH_EMAIL_LINK = 'signinWithEmailLink';
const ACTION_SEND_EMAIL_LINK = 'sendEmailLink';
const ACTION_SEND_PASSWORD_RESET = 'sendPasswordReset';
const ACTION_FETCH_ORGANIZATIONS = 'fetchOrganizations';
const ACTION_FETCH_USER_ORGANIZATION = 'fetchUserOrganization';
const ACTION_UNSUBSCRIBE_ORGANIZATIONS = 'unsubscribeOrganizations';
const ACTION_FETCH_ORGANIZATION_TAGS = 'fetchOrganizationTags';
const ACTION_MARK_USER_JOURNAL_READ = 'markUserJournalAsRead';
const ACTION_CREATE_ORGANIZATION = 'createOrganization';
const GETTER_IS_AUTHENTICATED = 'isAuthenticated';

// Temporary performance fix #ducktape
const GETTER_MAX_PORTFOLIO_SECTION_SIZE = 'maxPortfolioSectionSize';
const ACTION_FETCH_USER_PREFERENCES = 'fetchUserPreferences';
const ACTION_SET_MAX_PORTFOLIO_SECTION_SIZE = 'setMaxPortfolioSectionSize';
const MUTATION_USER_PREFERENCES = 'MUTATION_USER_PREFERENCES';

const GETTER_USER_ID = 'userId';
const GETTER_USER_ID_TOKEN = 'userIdToken';
const GETTER_USER_IS_ADMIN = 'isAdmin';
const GETTER_USER_FIREBASE_UID = 'firebaseUid';
const GETTER_USER_DISPLAY_NAME = 'displayName';
const GETTER_USER_JOURNAL_READ = 'userJournalRead';
const GETTER_ORGANIZATION_ID = 'organizationId';
const GETTER_ORGANIZATION_NAME = 'organizationName';
const GETTER_IS_IDTOKEN_READY = 'idTokenReady';
const GETTER_OBJECTIVES = 'objectives';
const GETTER_FLAT_OBJECTIVES = 'flatObjectives';
const GETTER_GROUPS = 'groups';
const GETTER_GROUP_NAME = 'groupName';
const GETTER_ORGANIZATIONS = 'organizations';
const GETTER_ORGANIZATION_TAGS = 'organizationTags';
const GETTER_FETCH_ORGANIZATION_TAGS_IN_PROGRESS = 'fetchOrganizationTagsInProgress';
const GETTER_IN_PROGRESS = 'inProgress';

const initialState = {
  user: JSON.parse(localStorage.getItem('user')), // TODO store and verify firebase project match
  userState: {},
  userPreferences: {},
  objectives: [],
  idTokenReady: false,
  fetchOrganizationTagsInProgress: false,
  organizationTags: [],
  organizationName: undefined,
  organizations: [],
  inProgress: false,
  userIdToken: undefined,
};

function flat(objectives, indentationLevel = 0) {
  let allObjectives = [];
  objectives.forEach(objective => {
    allObjectives.push({
      id: objective.id,
      name: '> '.repeat(indentationLevel * 2) + objective.name,
    });
    const sortedSubobjectives = sortBy(objective.subobjectives, subobjective => subobjective.name);
    allObjectives = allObjectives.concat(flat(sortedSubobjectives, indentationLevel + 1));
  });

  return allObjectives;
}

export const onSnapshotOrganizations = (commit, snapshot) => {
  const organizations = snapshot.docs.map(org => ({
    ...org.data(),
    id: org.id,
  }));

  commit(MUTATION_FETCH_ORGANIZATIONS, organizations);
};

export const onSnapshotWithTags = (commit, snapshot) => {
  const tags = [];
  snapshot.docs.forEach(tag => tags.push(tag.data()));
  commit(MUTATION_FETCH_ORGANIZATION_TAGS_FINISHED, tags);
};

export const onSnapshotUserPreferences = (commit, snapshot) => {
  const preferences = snapshot.exists ? snapshot.data() : {};
  commit(MUTATION_USER_PREFERENCES, preferences);
};

const getters = {
  [GETTER_IS_AUTHENTICATED]: state => !!state.user,
  [GETTER_USER_ID]: state => (state.user ? state.user.id : null),
  [GETTER_USER_ID_TOKEN]: state => (state.userIdToken),
  [GETTER_USER_IS_ADMIN]: state => (state.user ? state.user.isAdmin : null),
  [GETTER_USER_FIREBASE_UID]: state => (state.user ? state.user.uid : null),
  [GETTER_USER_DISPLAY_NAME]: state => (state.user ? state.user.displayName : null),
  [GETTER_USER_JOURNAL_READ]: state => (state.userState ? state.userState.journalRead : true),
  [GETTER_ORGANIZATION_ID]: state => (state.user ? state.user.organizationId : null),
  [GETTER_ORGANIZATION_NAME]: state => state.organizationName,
  [GETTER_IS_IDTOKEN_READY]: state => state.idTokenReady,
  [GETTER_OBJECTIVES]: state => (state.objectives),
  [GETTER_FLAT_OBJECTIVES]: state => (state.user ? state.user.objectives : []),
  [GETTER_GROUPS]: state => (state.user ? state.user.groups : []),
  [GETTER_GROUP_NAME]: state => (groupId) => {
    const matchingGroup = getters[GETTER_GROUPS](state).find(group => group.id === groupId);
    if (matchingGroup) {
      return matchingGroup.name;
    }

    return '';
  },
  [GETTER_FETCH_ORGANIZATION_TAGS_IN_PROGRESS]: state => state.fetchOrganizationTagsInProgress,
  [GETTER_ORGANIZATIONS]: state => state.organizations,
  [GETTER_ORGANIZATION_TAGS]: state => state.organizationTags,
  [GETTER_MAX_PORTFOLIO_SECTION_SIZE]: state => state.userPreferences.maxPortfolioSectionSize,
  [GETTER_IN_PROGRESS]: state => state.inProgress,
};

const mutations = {
  [MUTATION_LOGIN]: (state, user) => {
    state.user = user;
    state.idTokenReady = true;
    state.inProgress = false;
  },
  [MUTATION_LOGOUT]: state => {
    state.idTokenReady = false;
    state.user = undefined;
  },
  [MUTATION_OBJECTIVES]: (state, objectives) => {
    state.objectives = objectives;
  },
  [MUTATION_FETCH_ORGANIZATIONS]: (state, organizations) => {
    Vue.set(state, 'organizations', organizations);
  },
  [MUTATION_START_FETCH_ORGANIZATION_TAGS]: (state) => {
    state.fetchOrganizationTagsInProgress = true;
  },
  [MUTATION_FETCH_ORGANIZATION_TAGS_FINISHED]: (state, organizationTags) => {
    state.fetchOrganizationTagsInProgress = false;
    state.organizationTags = organizationTags;
  },
  [MUTATION_MARK_USER_JOURNAL_READ]: state => {
    state.user.journalRead = true;
  },
  [MUTATION_USER_STATE]: (state, userState) => {
    state.userState = userState;
  },
  [MUTATION_USER_PREFERENCES]: (state, preferences) => {
    Vue.set(state.userPreferences, 'maxPortfolioSectionSize', preferences.maxPortfolioSectionSize);
  },
  [MUTATION_START_LOGIN]: (state) => {
    state.inProgress = true;
  },
  [MUTATION_END_LOGIN]: (state) => {
    state.inProgress = false;
  },
  [MUTATION_SET_ID_TOKEN]: (state, userIdToken) => {
    state.userIdToken = userIdToken;
  },
  [MUTATION_ORGANIZATION_NAME]: (state, organizationName) => {
    state.organizationName = organizationName;
  },
};

function sortObjectivesAlphabetically(objectives) {
  const sortedObjectives = sortBy(objectives, objective => objective.name);

  sortedObjectives.forEach(objective => {
    objective.subobjectives = sortObjectivesAlphabetically(objective.subobjectives);
  });

  return sortedObjectives;
}

export function onCurrentUserDetailsSnapshot({ commit, dispatch }, { user, isAdmin }, snapshot) {
  const currentUserDetails = snapshot.data();
  const newUserObject = {
    ...user,
    id: currentUserDetails.id,
    organizationId: currentUserDetails.organizationId,
    objectives: flat(sortBy(currentUserDetails.objectives, objective => objective.name)),
    groups: currentUserDetails.groups ? currentUserDetails.groups.sort(first => (first.isPrivate ? -1 : 1)) : [],
    firstName: currentUserDetails.firstName,
    lastName: currentUserDetails.lastName,
    displayName: `${currentUserDetails.firstName} ${currentUserDetails.lastName}`,
    isAdmin,
  };

  localStorage.setItem('user', JSON.stringify(newUserObject));
  dispatch(featureSwitches.actions.firestoreUserSwitches, currentUserDetails.featureSwitches || {}, { root: true });
  commit(MUTATION_OBJECTIVES, sortObjectivesAlphabetically(currentUserDetails.objectives));
  commit(MUTATION_LOGIN, newUserObject);
  dispatch(ACTION_FETCH_USER_ORGANIZATION);
}

export function onCurrentUserStateSnapshot(commit, user, snapshot) {
  try {
    if (!snapshot.docs[0]) {
      // Reset state when no user data
      commit(MUTATION_USER_STATE, { journalRead: false });
      return;
    }

    const currentUserState = snapshot.docs[0].data();
    commit(MUTATION_USER_STATE, currentUserState);
  } catch (error) {
    console.error('Fail onCurrentUserStateSnapshot', error);
    commit(MUTATION_USER_STATE, { journalRead: false });
  }
}

const actions = {
  [ACTION_LOGIN]: async ({ commit, dispatch }, firebaseUser) => {
    const user = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
    };

    let isAdmin = false;

    if (getFirebaseAuth().currentUser) {
      const token = await getFirebaseAuth().currentUser.getIdTokenResult(false);
      isAdmin = token.claims.admin;
    }

    const query = firestoreDocumentById('users', user.uid);
    const unsubscribe = onSnapshot(query, onCurrentUserDetailsSnapshot.bind(this, { commit, dispatch }, { user, isAdmin }));

    const userState = {
      journalRead: false,
    };

    const colQuery = firestoreCollection(`users/${user.uid}/state`);
    const unsubscribeUserState = onSnapshot(colQuery, onCurrentUserStateSnapshot.bind(this, commit, userState));

    const updatedFirebaseIdToken = await getFirebaseAuth().currentUser.getIdToken(true);
    commit(MUTATION_SET_ID_TOKEN, updatedFirebaseIdToken);

    commit(
      MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: unsubscribe, id: `users.${user.uid}` }, { root: true },
      MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: unsubscribeUserState, id: 'userState' }, { root: true },
    );
  },
  [ACTION_LOGIN_EXTENSION]: async ({ commit, dispatch }, firebaseUser) => {
    const user = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      id: firebaseUser.uid,
    };

    const userDetailsRef = firestoreDocumentById('users', firebaseUser.uid);
    const userDetails = (await userDetailsRef.get()).data();

    if (userDetails.id) {
      user.id = userDetails.id;
    }

    user.displayName = `${userDetails.firstName} ${userDetails.lastName}`;
    user.organizationId = userDetails.organizationId;
    user.objectives = sortObjectivesAlphabetically(userDetails.objectives);
    user.groups = userDetails.groups.sort(first => (first.isPrivate ? -1 : 1));
    localStorage.setItem('user', JSON.stringify(user));
    commit(MUTATION_LOGIN, user);

    dispatch(ACTION_FETCH_USER_ORGANIZATION);
  },
  [ACTION_LOGOUT]: async ({ commit, dispatch }) => {
    dispatch('unsubscribeSnapshotListeners', {}, { root: true });
    localStorage.removeItem('user');
    await logout();
    commit(MUTATION_LOGOUT);
  },
  [ACTION_SIGNIN]: async (_, { email, password }) => login(email, password),
  [ACTION_SEND_EMAIL_LINK]: async (_, { email }) => {
    await sendSignInLink(email);
  },
  [ACTION_SEND_PASSWORD_RESET]: async (_, { email }) => {
    console.log('sending password reset to:', email);
    await sendPasswordReset(email);
  },
  [ACTION_SIGNIN_WITH_EMAIL_LINK]: async (context, { url, email, password }) => {
    if (isSignInLink(url)) {
      if (getFirebaseAuth().currentUser === null) {
        const result = await signInWithLink(email, url);
        await updatePass(result.user, password);
        await context.dispatch(types.actions.login, result.user);
      } else {
        await updatePass(getFirebaseAuth().currentUser, password);
        await context.dispatch(types.actions.login, getFirebaseAuth().currentUser);
      }
      return null;
    }
    const notFromEmailLinkError = { code: 'notFromEmailLink' };
    throw notFromEmailLinkError;
  },
  [ACTION_FETCH_ORGANIZATIONS]: async ({ commit }) => {
    const query = firestoreCollection('organizations');
    const unsubscribe = onSnapshot(query, onSnapshotOrganizations.bind(this, commit));
    commit(
      MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: unsubscribe, id: 'organizations' }, { root: true },
    );
  },
  [ACTION_FETCH_USER_ORGANIZATION]: async ({ state, commit }) => {
    const organization = await firestoreDocumentById('organizations', state.user.organizationId).get();
    commit(MUTATION_ORGANIZATION_NAME, organization.data().name);
  },
  [ACTION_UNSUBSCRIBE_ORGANIZATIONS]: async ({ commit }) => {
    commit(MUTATION_UNREGISTER_SNAPSHOT_LISTENER, { id: 'organizations' }, { root: true });
  },
  [ACTION_CREATE_ORGANIZATION]: async (_, name) => {
    const documentId = createNewId();
    await set(firestoreDocumentById('organizations', documentId), { name });
  },
  [ACTION_FETCH_ORGANIZATION_TAGS]: async ({ commit, getters: authenticationGetters }) => {
    commit(MUTATION_START_FETCH_ORGANIZATION_TAGS);
    const { organizationId } = authenticationGetters;
    const query = firestoreCollection(`organizations/${organizationId}/tags`);
    const unsubscribe = onSnapshot(query, onSnapshotWithTags.bind(this, commit));
    commit(
      MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: unsubscribe, id: 'organizations.tags' }, { root: true },
    );
  },
  [ACTION_MARK_USER_JOURNAL_READ]: async ({ getters: authenticationGetters }) => {
    const { userId } = authenticationGetters;
    const qs = await firestoreCollection(`/users/${userId}/state`).get();
    if (!qs.empty) {
      const stateDoc = qs.docs[0];
      stateDoc.ref.update({ journalRead: true });
    }
  },
  [ACTION_FETCH_USER_PREFERENCES]: async ({ commit, getters: authenticationGetters }) => {
    const { userId } = authenticationGetters;
    const query = firestoreDocumentById(`/users/${userId}/details`, 'preferences');
    const unsubscribe = onSnapshot(query, onSnapshotUserPreferences.bind(this, commit));
    commit(
      MUTATION_REGISTER_SNAPSHOT_LISTENER,
      { snapshotListener: unsubscribe, id: 'user.details.preferences' },
      { root: true },
    );
  },
  [ACTION_SET_MAX_PORTFOLIO_SECTION_SIZE]: async ({ getters: authenticationGetters }, maxPortfolioSectionSize) => {
    const { userId } = authenticationGetters;
    const body = {};

    if (!maxPortfolioSectionSize) {
      body.maxPortfolioSectionSize = getFieldValue().delete();
    } else {
      body.maxPortfolioSectionSize = maxPortfolioSectionSize;
    }

    await setMerge(firestoreDocumentById(`/users/${userId}/details`, 'preferences'), body);
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
};

export const types = {
  getters: {
    isAuthenticated: `${MODULE_PREFIX}/${GETTER_IS_AUTHENTICATED}`,
    userJournalRead: `${MODULE_PREFIX}/${GETTER_USER_JOURNAL_READ}`,
    organizations: `${MODULE_PREFIX}/${GETTER_ORGANIZATIONS}`,
    organizationId: `${MODULE_PREFIX}/${GETTER_ORGANIZATION_ID}`,
    organizationName: `${MODULE_PREFIX}/${GETTER_ORGANIZATION_NAME}`,
    idTokenReady: `${MODULE_PREFIX}/${GETTER_IS_IDTOKEN_READY}`,
    userId: `${MODULE_PREFIX}/${GETTER_USER_ID}`,
    isAdmin: `${MODULE_PREFIX}/${GETTER_USER_IS_ADMIN}`,
    firebaseUid: `${MODULE_PREFIX}/${GETTER_USER_FIREBASE_UID}`,
    displayName: `${MODULE_PREFIX}/${GETTER_USER_DISPLAY_NAME}`,
    objectives: `${MODULE_PREFIX}/${GETTER_OBJECTIVES}`,
    flatObjectives: `${MODULE_PREFIX}/${GETTER_FLAT_OBJECTIVES}`,
    groups: `${MODULE_PREFIX}/${GETTER_GROUPS}`,
    groupName: `${MODULE_PREFIX}/${GETTER_GROUP_NAME}`,
    organizationTags: `${MODULE_PREFIX}/${GETTER_ORGANIZATION_TAGS}`,
    fetchOrganizationTagsInProgress: `${MODULE_PREFIX}/${GETTER_FETCH_ORGANIZATION_TAGS_IN_PROGRESS}`,
    maxPortfolioSectionSize: `${MODULE_PREFIX}/${GETTER_MAX_PORTFOLIO_SECTION_SIZE}`,
    inProgress: `${MODULE_PREFIX}/${GETTER_IN_PROGRESS}`,
  },
  mutations: {
    LOGIN: `${MODULE_PREFIX}/${MUTATION_LOGIN}`,
    LOGOUT: `${MODULE_PREFIX}/${MUTATION_LOGOUT}`,
    USER_PREFERENCES: `${MODULE_PREFIX}/${MUTATION_USER_PREFERENCES}`,
    START_LOGIN: `${MODULE_PREFIX}/${MUTATION_START_LOGIN}`,
    END_LOGIN: `${MODULE_PREFIX}/${MUTATION_END_LOGIN}`,
  },
  actions: {
    login: `${MODULE_PREFIX}/${ACTION_LOGIN}`,
    loginExtension: `${MODULE_PREFIX}/${ACTION_LOGIN_EXTENSION}`,
    logout: `${MODULE_PREFIX}/${ACTION_LOGOUT}`,
    signin: `${MODULE_PREFIX}/${ACTION_SIGNIN}`,
    signinWithEmailLink: `${MODULE_PREFIX}/${ACTION_SIGNIN_WITH_EMAIL_LINK}`,
    sendEmailLink: `${MODULE_PREFIX}/${ACTION_SEND_EMAIL_LINK}`,
    sendPasswordReset: `${MODULE_PREFIX}/${ACTION_SEND_PASSWORD_RESET}`,
    fetchOrganizations: `${MODULE_PREFIX}/${ACTION_FETCH_ORGANIZATIONS}`,
    unsubscribeOrganizations: `${MODULE_PREFIX}/${ACTION_UNSUBSCRIBE_ORGANIZATIONS}`,
    fetchOrganizationTags: `${MODULE_PREFIX}/${ACTION_FETCH_ORGANIZATION_TAGS}`,
    createOrganization: `${MODULE_PREFIX}/${ACTION_CREATE_ORGANIZATION}`,
    markUserJournalAsRead: `${MODULE_PREFIX}/${ACTION_MARK_USER_JOURNAL_READ}`,
    setMaxPortfolioSectionSize: `${MODULE_PREFIX}/${ACTION_SET_MAX_PORTFOLIO_SECTION_SIZE}`,
    fetchUserPreferences: `${MODULE_PREFIX}/${ACTION_FETCH_USER_PREFERENCES}`,
  },
};
